import styled from '@emotion/styled';
import moment from 'moment';
import React, { FC, useEffect, useRef, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
    getPastVideoSessions,
    getSessionUtilizationInfo,
    getUpcomingVideoSessions,
} from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import {
    ActionItemList,
    ActionPropsWithId,
    BodyTextSmall,
    ComponentTextMedium,
    dsColors,
    Modal,
} from 'src/shared/ds';
import { pxToRem } from 'src/shared/helpers/pxToRem';
import {
    getMyProviders,
    getMyTherapists,
} from 'src/modules/providers/selectors/providersSelectors';

interface ForYouModalProps {
    handleClose: VoidFunction;
    forYouActions: ActionPropsWithId[];
}

enum Tabs {
    ALL = 'ALL',
    UPCOMING = 'UPCOMING',
    PAST = 'PAST',
}

export const ForYouModal: FC<ForYouModalProps> = ({ handleClose, forYouActions }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(Tabs.ALL);
    const upcomingSessions = useSelector(getUpcomingVideoSessions);
    const pastSessions = useSelector(getPastVideoSessions);
    const myProviders = useSelector(getMyProviders);
    const myTherapists = useSelector(getMyTherapists);
    const { coachingVisitsRemaining, therapyVisitsRemaining, resetDate } = useSelector(
        getSessionUtilizationInfo,
    );
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [contentHeight, setContentHeight] = useState<number | null>(null);
    const shouldDisplayInfoBar = myProviders.length > 0;
    useEffect(() => {
        if (contentRef.current?.clientHeight) {
            setContentHeight(contentRef.current?.clientHeight);
        }
    }, [activeTab]);

    const displayForYouActions = () => (
        <ActionItemList title={t('dashboard:for_you.actions')} actions={forYouActions} />
    );

    const displayUpcoming = () => (
        <ActionItemList
            title={t(
                `dashboard:for_you.${
                    upcomingSessions.length > 0 ? 'upcoming_sessions' : 'no_upcoming_sessions'
                }`,
            )}
            actions={upcomingSessions.map(session => ({
                id: session.uuid,
                onClick: () => null,
                title: t('dashboard:for_you.coaching_with', {
                    name: session.coachAssignment?.coach?.firstName,
                }),
                description: moment(session.scheduledTime).format('dddd, MMMM D, YYYY'),
                startIconUrl: session.coachAssignment?.coach?.avatar ?? undefined,
                startIconBackgroundColor: 'blue800',
            }))}
        />
    );

    const displayPast = () => (
        <ActionItemList
            title={t(
                `dashboard:for_you.${
                    pastSessions.length > 0 ? 'past_sessions' : 'no_past_sessions'
                }`,
            )}
            actions={pastSessions.map(session => ({
                id: session.uuid,
                onClick: () => null,
                title: t('dashboard:for_you.coaching_with', {
                    name: session.coachAssignment?.coach?.firstName,
                }),
                description: moment(session.scheduledTime).format('dddd, MMMM D, YYYY'),
                startIconUrl: session.coachAssignment?.coach?.avatar ?? undefined,
                startIconBackgroundColor: 'blue800',
                endIconName: 'checkmark',
                endIconColor: 'cyan500',
                startIconOpacityReduced: true,
                initials: `${session.coachAssignment?.coach?.firstName?.[0] ?? ''}${
                    session.coachAssignment?.coach?.lastName?.[0] ?? ''
                }
                `,
            }))}
        />
    );

    const tabs = [
        {
            value: Tabs.ALL,
            label: t('dashboard:for_you.tabs.all'),
            displayContent: () => (
                <>
                    {displayForYouActions()}
                    {displayUpcoming()}
                    {displayPast()}
                </>
            ),
        },
        {
            value: Tabs.UPCOMING,
            label: t('dashboard:for_you.tabs.upcoming'),
            displayContent: displayUpcoming,
        },
        {
            value: Tabs.PAST,
            label: t('dashboard:for_you.tabs.past'),
            displayContent: displayPast,
        },
    ];

    const displayInfoBarText = () => {
        const dateFormat = 'MM/DD/YYYY';
        if (coachingVisitsRemaining === 0 && therapyVisitsRemaining === 0) {
            return t('dashboard:for_you.no_remaining_sessions', {
                date: resetDate.format(dateFormat),
            });
        }

        if (myTherapists.length > 0) {
            return t('dashboard:for_you.remaining_sessions_with_therapy', {
                coachingVisitsRemaining,
                therapyVisitsRemaining,
                date: resetDate.format(dateFormat),
            });
        }

        return t('dashboard:for_you.remaining_sessions_coaching_only', {
            coachingVisitsRemaining,
            date: resetDate.format(dateFormat),
        });
    };

    return (
        <Modal
            title={t('dashboard:for_you.title')}
            description={t('dashboard:for_you.description')}
            handleClose={handleClose}
        >
            {shouldDisplayInfoBar && (
                <InfoBar>
                    <BodyTextSmall>{displayInfoBarText()}</BodyTextSmall>
                </InfoBar>
            )}
            <TabBar>
                {tabs.map(tab => {
                    const isActive = tab.value === activeTab;
                    return (
                        <TabBarElement
                            css={[isActive && { backgroundColor: dsColors.blue500 }]}
                            onClick={() => setActiveTab(tab.value)}
                            key={tab.value}
                        >
                            <ComponentTextMedium fontColor={isActive ? 'white' : 'neutral200'}>
                                {tab.label}
                            </ComponentTextMedium>
                        </TabBarElement>
                    );
                })}
            </TabBar>
            <AnimateHeight
                duration={200}
                height={contentHeight ?? 'auto'}
                style={{
                    width: '100%',
                    marginTop: pxToRem(32),
                }}
            >
                <TabBarContent ref={contentRef}>
                    {tabs.find(tab => tab.value === activeTab)?.displayContent()}
                </TabBarContent>
            </AnimateHeight>
        </Modal>
    );
};

const InfoBar = styled.div({
    backgroundColor: dsColors.neutral900,
    borderRadius: pxToRem(4),
    padding: pxToRem(12),
    marginBottom: pxToRem(16),
});

const TabBar = styled.div({
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
});

const TabBarElement = styled.div({
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    padding: `${pxToRem(12)} 0`,
    backgroundColor: dsColors.neutral900,
    cursor: 'pointer',
    userSelect: 'none',

    '&:first-of-type': {
        borderTopLeftRadius: pxToRem(4),
        borderBottomLeftRadius: pxToRem(4),
    },
    '&:last-child': {
        borderTopRightRadius: pxToRem(4),
        borderBottomRightRadius: pxToRem(4),
    },
});

const TabBarContent = styled.div({
    height: 'auto',
    width: '100%',
    maxHeight: '40vh',
    overflowY: 'auto',
    marginRight: pxToRem(-20),
    paddingRight: pxToRem(20),

    '&::-webkit-scrollbar-thumb': {
        borderRadius: pxToRem(12),
        background: dsColors.neutral700,
        width: pxToRem(6),
    },
    '&::-webkit-scrollbar-track': {
        background: dsColors.neutral900,
        borderRadius: pxToRem(100),
        width: pxToRem(6),
    },
});
